"use client";

import { StatsTable } from "fansunited-sports-ui";
import { mapStandingsToTable, DEFAULT_COLUMNS } from "@/utilities/mappers/standings";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { SITE_TYPE_FOOTBALL_TEAM } from "@/config/siteTypes";
import { StageTabs } from "@/components/ui/StageTabs";
import type { FUSportsStandingsWithNativeTeamIds, StageStandings } from "@/utilities/data/types";

const tableStyles = {
    borderRadius: "md" as const,
    shadow: "md" as const,
    typography: {
        cell: "text-sm font-medium text-foreground",
        header: "text-xs uppercase font-bold text-muted-foreground",
    },
};

interface LiveTableTabProps {
    /**
     * All available stages with their standings. If omitted or empty, falls back
     * to the primary `standings` (single-stage path).
     */
    allStageStandings?: StageStandings[];
    standings: FUSportsStandingsWithNativeTeamIds | undefined;
}

export function LiveTableTab({ allStageStandings, standings }: LiveTableTabProps) {
    const labels = useLabels().standings;
    const nav = useNavigation();
    const { siteType } = useTenantSettings();
    const teamHref = siteType === SITE_TYPE_FOOTBALL_TEAM ? undefined : nav.team;

    // Multi-stage path: render one StatsTable per stage, switched via StageTabs.
    // The tab strip and per-stage table share a single rounded card wrapper, so the
    // border radius is continuous (no double-card seam between strip and table).
    if (allStageStandings && allStageStandings.length > 0) {
        const tabs = allStageStandings.map((s) => ({ id: s.stage.id, label: s.stage.name }));
        const flatTableStyles = {
            ...tableStyles,
            borderRadius: "none" as const,
            shadow: "none" as const,
        };

        const renderTable = (stageId: string) => {
            const found = allStageStandings.find((s) => s.stage.id === stageId);
            if (!found) return null;
            const t = mapStandingsToTable(found.standings, labels, DEFAULT_COLUMNS, undefined, teamHref);
            return (
                <StatsTable columns={t.columns} rows={t.rows} legend={t.legend} showLegend styles={flatTableStyles} />
            );
        };

        return (
            <div className="bg-card border border-border rounded-md shadow-md overflow-hidden">
                <StageTabs tabs={tabs} renderBody={renderTable} />
            </div>
        );
    }

    // Legacy single-stage path (kept for safety if a caller forgets to pass allStageStandings).
    if (!standings) return null;
    const mainTable = mapStandingsToTable(standings, labels, DEFAULT_COLUMNS, undefined, teamHref);

    return (
        <StatsTable
            columns={mainTable.columns}
            rows={mainTable.rows}
            legend={mainTable.legend}
            showLegend
            styles={tableStyles}
        />
    );
}
